/**
 * A client's answers to a form, read-only, for staff: what changed since last year first, then
 * each section; repeating groups (W-2s, dependents) as tables; Social Security, EIN and bank
 * numbers masked (the server sends them in full only when "Show" is chosen, and records it); and
 * the whole set as a spreadsheet for the tax software. Read-only users get neither.
 */
import { SENSITIVE_TYPES, fillYear, isIndividualReturnOrganizer, formatValue, rowsOf, visibleQuestions, type Answers, type OrganizerDefinition, type Question, type Scalar } from "@clientflow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Eye, EyeOff, FileText, History } from "lucide-react";
import { useState } from "react";
import { Loading } from "@/components/page";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { hasPermission, useMe } from "@/lib/session";
import type { Doc } from "@/lib/types";
import { toast } from "@/lib/use-toast";
import { TableScroll } from "@/components/table-scroll";

export type FormDetail = {
  id: string;
  title: string;
  status: string;
  taxYear: number;
  definition: OrganizerDefinition;
  answers: Answers;
  lastYear: { title: string; taxYear: number; changed: string[]; notListed: string[]; added: string[] } | null;
  clientId?: string;
  kind?: "organizer" | "intake";
  /** Started from last year's return: its year, the document, and a note per question it answered or spoke to. */
  fromReturn?: { taxYear: number | null; documentId: string | null; readAt: string | null; hints: Record<string, string> } | null;
};

const READABLE = ["application/pdf", "image/jpeg", "image/png", "image/gif", "image/webp"];

/** Staff: start an open tax organizer from last year's return, read by the document reader. */
function FromReturn({ data }: { data: FormDetail }) {
  const qc = useQueryClient();
  const { data: docs, error: docsError } = useQuery<Doc[]>({ queryKey: ["/api/documents?clientId=", data.clientId], enabled: !!data.clientId });
  const prev = data.taxYear - 1;
  const readable = (docs ?? []).filter((d) => READABLE.includes(d.contentType));
  // Last year's return is filed in this year's Tax Returns folder (a 2026 return, filed in 2027).
  const likely = readable.find((d) => d.folder === "Tax Returns" && d.year === data.taxYear) ?? readable.find((d) => new RegExp(`\\b${prev}\\b.*(1040|return)|(1040|return).*\\b${prev}\\b`, "i").test(d.filename));
  const [doc, setDoc] = useState("");
  const chosen = doc || likely?.id || "";
  const fill = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ filled: string[]; returnYear: number }>("POST", `/api/organizers/${data.id}/from-return`, { documentId: chosen }),
    onSuccess: (r) => {
      toast({ title: `Filled from the ${r.returnYear} return`, description: r.filled.length ? `${r.filled.length} question${r.filled.length === 1 ? "" : "s"} answered for the client to confirm. Their own answers were not changed.` : "No unanswered questions to fill; notes were added where the return applies." });
      void qc.invalidateQueries({ queryKey: ["/api/organizers/", data.id] });
    },
  });
  const fr = data.fromReturn;
  return (
    <div className="rounded-lg border border-border p-3 text-sm">
      <p className="mb-1 flex items-center gap-1.5 font-semibold">
        <FileText className="h-4 w-4" aria-hidden /> Start from last year's return
      </p>
      <p className="mb-3 text-xs text-muted-foreground">
        The document reader reads the client's {prev} return. Questions that carry over from year to year (filing status, dependents, rental property, mortgage, direct deposit and others) are answered for the client to confirm; questions about this year get a note saying what last year's return showed. The client's own answers are not changed. Needs the client's AI consent.
      </p>
      {fr && <p className="mb-2 text-xs">Filled from the {fr.taxYear} return{fr.readAt ? ` on ${formatDateTime(fr.readAt)}` : ""}.</p>}
      {docsError ? (
        <p className="text-xs text-destructive">The client's documents couldn't be loaded. {errorMessage(docsError)}</p>
      ) : !readable.length ? (
        <p className="text-xs text-muted-foreground">No PDF or photo on file for this client yet. Upload last year's return to their Tax Returns folder first.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Last year's return" className="h-9 min-w-0 max-w-full rounded-md border border-input bg-background px-2 text-sm" value={chosen} onChange={(e) => setDoc(e.target.value)}>
            {!chosen && <option value="">Choose the {prev} return</option>}
            {readable.map((d) => (
              <option key={d.id} value={d.id}>
                {d.filename} ({d.folder}, {d.year})
              </option>
            ))}
          </select>
          <Button size="sm" variant="outline" disabled={!chosen || fill.isPending} onClick={() => fill.mutate()}>
            {fill.isPending ? "Reading…" : fr ? "Read again" : "Fill from this return"}
          </Button>
        </div>
      )}
      {fill.error && <p className="mt-2 text-destructive">{errorMessage(fill.error)}</p>}
    </div>
  );
}

const sensitive = (t: string) => (SENSITIVE_TYPES as readonly string[]).includes(t);

export function FormAnswers({ id }: { id: string }) {
  const { data } = useQuery<FormDetail>({ queryKey: ["/api/organizers/", id] });
  return data ? <FormAnswersBody data={data} /> : <Loading />;
}

export function FormAnswersBody({ data }: { data: FormDetail }) {
  const [reveal, setReveal] = useState(false);
  const { data: me } = useMe();
  const preparer = hasPermission(me, "engagements.write");
  // Full numbers are fetched only while shown, and not kept afterwards.
  const full = useQuery<FormDetail>({ queryKey: ["/api/organizers/", data.id, "?reveal=1"], enabled: reveal && preparer, gcTime: 0, staleTime: 0 });
  const answers = reveal && full.data ? full.data.answers : data.answers;
  const visible = new Set(visibleQuestions(data.definition, answers).map((q) => q.key));
  const hasSensitive = data.definition.sections.some((s) => s.questions.some((q) => sensitive(q.type) || (q.fields ?? []).some((f) => sensitive(f.type))));
  const show = (spec: { type: string; options?: Array<{ value: string; label: string }> }, v: Scalar | undefined) => {
    if (v === undefined || v === "") return <span className="text-muted-foreground">Not answered</span>;
    if (spec.type === "date" && typeof v === "string") return formatDate(v);
    return <span className="whitespace-pre-wrap">{formatValue(spec, v, reveal && !!full.data)}</span>;
  };
  const ly = data.lastYear;
  const notes = data.fromReturn?.hints ?? {};
  const fill = (text: string) => fillYear(text, data.taxYear);
  return (
    <div className="min-w-0 space-y-5">
      {preparer && (
        <div className="flex flex-wrap items-center gap-2">
          <a href={`/api/organizers/${data.id}/export.csv`} className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-muted">
            <Download className="h-4 w-4" /> Download answers (CSV)
          </a>
          {hasSensitive && (
            <Button size="sm" variant="ghost" disabled={reveal && full.isFetching} onClick={() => setReveal((r) => !r)}>
              {reveal ? <EyeOff className="mr-1.5 h-4 w-4" /> : <Eye className="mr-1.5 h-4 w-4" />}
              {reveal ? "Hide protected numbers" : "Show protected numbers"}
            </Button>
          )}
        </div>
      )}

      {hasPermission(me, "requests.write") && data.status === "open" && data.kind !== "intake" && isIndividualReturnOrganizer(data.definition) && <FromReturn data={data} />}

      {ly && (ly.changed.length + ly.notListed.length + ly.added.length > 0) && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700/50 dark:bg-amber-950/30">
          <p className="mb-1.5 flex items-center gap-1.5 font-semibold">
            <History className="h-4 w-4" aria-hidden /> Since last year ({ly.taxYear})
          </p>
          <ul className="list-disc space-y-0.5 pl-5">
            {ly.notListed.map((x) => (
              <li key={`n${x}`}>
                <span className="font-medium">Not listed this year:</span> {x}
              </li>
            ))}
            {ly.added.map((x) => (
              <li key={`a${x}`}>
                <span className="font-medium">New:</span> {x}
              </li>
            ))}
            {ly.changed.map((x) => (
              <li key={`c${x}`}>{x}</li>
            ))}
          </ul>
        </div>
      )}

      {data.definition.sections.map((sec) => {
        const qs = sec.questions.filter((q) => visible.has(q.key));
        if (!qs.length) return null;
        return (
          <div key={sec.key}>
            <p className="mb-2 text-sm font-semibold">{sec.title}</p>
            <div className="divide-y divide-border rounded-md border border-border text-sm">
              {qs.map((q) =>
                q.type === "group" ? (
                  <GroupAnswer key={q.key} q={q} rows={rowsOf(answers[q.key])} show={show} fill={fill} note={notes[q.key]} />
                ) : (
                  <div key={q.key} className="grid gap-1 px-3 py-2 sm:grid-cols-[3fr_2fr]">
                    <p className="text-muted-foreground">
                      {fill(q.label)}
                      {notes[q.key] && <span className="mt-0.5 block text-xs text-primary">{notes[q.key]}</span>}
                    </p>
                    <p>{show(q, answers[q.key] as Scalar | undefined)}</p>
                  </div>
                ),
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GroupAnswer({ q, rows, show, fill, note }: { q: Question; rows: ReturnType<typeof rowsOf>; show: (spec: { type: string; options?: Array<{ value: string; label: string }> }, v: Scalar | undefined) => React.ReactNode; fill: (t: string) => string; note?: string }) {
  const fields = q.fields ?? [];
  return (
    <div className="min-w-0 px-3 py-2">
      <p className="mb-1.5 text-muted-foreground">
        {fill(q.label)} <span className="tabular">({rows.length})</span>
      </p>
      {note && <p className="-mt-1 mb-1.5 text-xs text-primary">{note}</p>}
      {rows.length === 0 ? (
        <p className="text-muted-foreground">None listed</p>
      ) : (
        <TableScroll label={`${fill(q.label)} table`}>
          <table className="w-full min-w-[480px] text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-1.5 pr-3 font-medium">#</th>
                {fields.map((f) => (
                  <th key={f.key} className="py-1.5 pr-3 font-medium">
                    {fill(f.label)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="tabular py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
                  {fields.map((f) => (
                    <td key={f.key} className="py-1.5 pr-3 align-top">
                      {show(f, r[f.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </div>
  );
}
